import { createLogger } from "../Logger";
import { IXhrResponseWrapper, Xhr } from "../xhr";
import { IZeosApiResponse } from "./contracts/ZeosApi";
import { IZeosApiConfig } from "./IZeosApiConfig";

export const REQUEST_ERROR = "RequestError";

let apiConfig: IZeosApiConfig;

const log = createLogger("ZeosApiExecutor");

export const init = async (config: IZeosApiConfig) => {
  apiConfig = config;
};

export const prepCallAsync = async () => {
  Xhr.baseSettings(
    apiConfig.apiUrl,
    apiConfig.apiVersion,
    apiConfig.apiKey,
    apiConfig.doEcho
  );
  const token = await apiConfig.apiAuthToken();
  Xhr.addRequestHeader("Authorization", `Bearer ${token}`);
};

export const handleError = <T>(
  funcName: string,
  endpoint: string,
  error: any,
  data: T | undefined = undefined
) => {
  log.info(funcName + " error", endpoint, error);
  const apiMessage =
    error?.data?.message ??
    error?.data?.Message ??
    error?.data?.error?.message ??
    error?.data?.error ??
    error?.statusText;
  const errorResponse: IZeosApiResponse<T> = {
    zeosStatus: "",
    message: apiMessage || REQUEST_ERROR,
    data: data as T,
    pagination: undefined,
  };
  return errorResponse;
};

// Failed calls arrive as the axios response (status on it) or, defensively, wrapped once more.
const errorHttpStatus = (errorResponse: any): number | undefined => {
  const status = errorResponse?.status ?? errorResponse?.response?.status;
  return typeof status === "number" ? status : undefined;
};

const normalizeApiPayload = <T>(payload: any): IZeosApiResponse<T> => {
  return {
    zeosStatus: payload?.zeosStatus ?? payload?.ZeosStatus ?? "",
    message: payload?.message ?? payload?.Message ?? "",
    data: payload?.data ?? payload?.Data,
    pagination: payload?.pagination ?? payload?.Pagination,
    errorRef: payload?.errorRef ?? payload?.ErrorRef,
  };
};

export async function getDataAsync<ResponseDataType>(
  endpoint: string,
  dataName: string
): Promise<IZeosApiResponse<ResponseDataType>> {
  await prepCallAsync();
  let uiPayload: IZeosApiResponse<ResponseDataType>;
  try {
    const xhrResponse: IXhrResponseWrapper = await Xhr.getAsync(endpoint);
    if (xhrResponse.zeosStatus !== "success") {
      const errorPayload =
        xhrResponse.errorResponse?.data ??
        xhrResponse.errorResponse?.response?.data ??
        xhrResponse.errorResponse;
      const apiPayload = normalizeApiPayload<ResponseDataType>(errorPayload);
      return {
        zeosStatus: apiPayload.zeosStatus || "error",
        message: apiPayload.message || REQUEST_ERROR,
        data: apiPayload.data as ResponseDataType,
        pagination: apiPayload.pagination,
        errorRef: apiPayload.errorRef,
        httpStatus: errorHttpStatus(xhrResponse.errorResponse),
      };
    }
    const apiPayload = normalizeApiPayload<ResponseDataType>(
      xhrResponse.rawResponse?.data
    );
    //TODO: Process the apiResponse's status message, other pre-model tasks
    const finalData = dataName
      ? (apiPayload.data as any)?.[dataName] ?? apiPayload.data
      : apiPayload.data;
    uiPayload = {
      zeosStatus: apiPayload.zeosStatus,
      message: apiPayload.message,
      data: finalData,
      pagination: apiPayload.pagination,
      errorRef: apiPayload.errorRef,
    };
  } catch (error: any) {
    uiPayload = handleError<ResponseDataType>(
      "getDataAsync",
      endpoint,
      error?.response?.data ?? error?.response ?? error
    );
  }
  return uiPayload;
}

export async function downloadDataAsync(
  endpoint: string
): Promise<IXhrResponseWrapper> {
  await prepCallAsync();
  let xhrResponse: IXhrResponseWrapper;
  try {
    xhrResponse = await Xhr.downloadAsync(endpoint);
  } catch (error: any) {
    xhrResponse = {
      zeosStatus: "error",
      errorResponse: error,
    };
  }
  return xhrResponse;
}

export async function handlePostPutAsync<ResponseDataType>(
  endpoint: string,
  data: any,
  returnDataName: string | null,
  isPost: boolean,
  isMultipartForm: boolean
) {
  await prepCallAsync();
  let uiPayload: IZeosApiResponse<ResponseDataType>;
  try {
    let xhrResponse;
    // Multipart callers pass FormData via isMultipartForm. Do not set
    // Content-Type: Axios/the browser must add multipart/form-data with the
    // boundary. A bare "multipart/form-data" header breaks form binding.
    void isMultipartForm;
    const headers = {};
    if (isPost) {
      xhrResponse = await Xhr.postAsync(endpoint, data, headers);
    } else {
      xhrResponse = await Xhr.putAsync(endpoint, data, headers);
    }
    if (xhrResponse.zeosStatus !== "success") {
      const errorPayload =
        xhrResponse.errorResponse?.data ??
        xhrResponse.errorResponse?.response?.data ??
        xhrResponse.errorResponse;
      const apiPayload = normalizeApiPayload<ResponseDataType>(errorPayload);
      return {
        zeosStatus: apiPayload.zeosStatus || "error",
        message: apiPayload.message || REQUEST_ERROR,
        data: apiPayload.data as ResponseDataType,
        pagination: apiPayload.pagination,
        errorRef: apiPayload.errorRef,
        httpStatus: errorHttpStatus(xhrResponse.errorResponse),
      };
    }
    const apiPayload = normalizeApiPayload<ResponseDataType>(
      xhrResponse.rawResponse?.data
    );
    const finalData = returnDataName
      ? (apiPayload.data as any)?.[returnDataName] ?? apiPayload.data
      : apiPayload.data;
    uiPayload = {
      zeosStatus: apiPayload.zeosStatus,
      message: apiPayload.message,
      data: finalData,
      pagination: apiPayload.pagination,
      errorRef: apiPayload.errorRef,
    };
  } catch (error: any) {
    uiPayload = handleError(
      isPost ? "postDataAsync" : "putDataAsync",
      endpoint,
      error?.response?.data ?? error?.response ?? error
    );
  }
  return uiPayload;
}

export async function postDataAsync<ResponseDataType>(
  endpoint: string,
  data: any,
  isMultipartForm: boolean = false
): Promise<IZeosApiResponse<ResponseDataType>> {
  return await handlePostPutAsync<ResponseDataType>(
    endpoint,
    data,
    null,
    true,
    isMultipartForm
  );
}

export async function postDataReturnAsync<ResponseDataType>(
  endpoint: string,
  data: any,
  returnDataName: string,
  isMultipartForm: boolean = false
): Promise<IZeosApiResponse<ResponseDataType>> {
  return await handlePostPutAsync<ResponseDataType>(
    endpoint,
    data,
    returnDataName,
    true,
    isMultipartForm
  );
}

export async function putDataAsync<ResponseDataType>(
  endpoint: string,
  data: any,
  isMultipartForm: boolean = false
): Promise<IZeosApiResponse<ResponseDataType>> {
  return await handlePostPutAsync<ResponseDataType>(
    endpoint,
    data,
    null,
    false,
    isMultipartForm
  );
}

export async function putDataReturnAsync<ResponseDataType>(
  endpoint: string,
  data: any,
  returnDataName: string,
  isMultipartForm: boolean = false
): Promise<IZeosApiResponse<ResponseDataType>> {
  return await handlePostPutAsync<ResponseDataType>(
    endpoint,
    data,
    returnDataName,
    false,
    isMultipartForm
  );
}

//TODO: HAVE TO DETERMINE IF SHOULD USE XHR.delDataAsync() INSTEAD, I.E., IF WE REALLY NEED THE ABILITY TO PASS DATA TO THE DELETE CALL
export async function deleteDataAsync<ResponseDataType>(
  endpoint: string,
  data: any
) {
  await prepCallAsync();
  let uiPayload: IZeosApiResponse<ResponseDataType>;
  try {
    let xhrResponse;
    xhrResponse = await Xhr.deleteAsync(endpoint, data);
    const apiPayload = normalizeApiPayload<ResponseDataType>(
      xhrResponse.rawResponse?.data
    );
    uiPayload = {
      zeosStatus: apiPayload.zeosStatus,
      message: apiPayload.message,
      data: apiPayload.data,
      pagination: apiPayload.pagination,
      errorRef: apiPayload.errorRef,
    };
  } catch (error: any) {
    uiPayload = handleError("deleteDataAsync", endpoint, error.response);
  }
  return uiPayload;
}

// export async function processDeleteAsync<ResponseDataType>(endpoint: string) {
//   await prepCallAsync();
//   let uiPayload: IDataWrapper<ResponseDataType>;
//   try {
//     let xhrResponse;
//     xhrResponse = await Xhr.delAsync(endpoint);
//     const apiPayload: IZeosApiResponse<ResponseDataType> =
//       xhrResponse.rawResponse?.data;
//     uiPayload = {
//       message: apiPayload.message,
//       data: apiPayload.data,
//     };
//   } catch (error: any) {
//     uiPayload = handleError("deleteDataAsync", endpoint, error.response);
//   }
//   return uiPayload;
// }
